import { v4 } from "uuid";

import {
  API_URL,
  put,
  call,
  take,
  select,
  debug,
  takeEvery,
  createDoRequestAction,
  CreateDoRequestActionOutput,
  SagaIterator,
} from "../../lib";
import { baseRequest, BaseRequestResult } from "../../lib/request";
import { REFRESH_REQUEST_TIMEOUT_MS } from "../../lib/auth/refresh_token_action";
import {
  currentSessionScope,
  isSessionScopeCurrent,
  sessionScopeStatus,
} from "../../lib/auth/session_scope";
import { observeSessionDeadlines, startDeadlineRequest } from "../../lib/auth/session_deadlines";
import { ActivityReportOutcome } from "../../lib/auth/activity_reporter";
import {
  AUTH_REFRESH_UNAVAILABLE,
  AUTH_REFRESHING_FAILURE,
  AUTH_SET,
  doAUTH_SESSION_DEADLINES_SET,
} from "../state";
import { doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT } from "./identity_refresh_token_submit";
import { getToken } from "./token_fetcher";

export const REQUEST_IDENTITY_SESSION_ACTIVITY = "REQUEST_IDENTITY_SESSION_ACTIVITY";
export const doREQUEST_IDENTITY_SESSION_ACTIVITY = createDoRequestAction<Record<string, never>>(
  REQUEST_IDENTITY_SESSION_ACTIVITY,
);

export const requestIdentitySessionActivityActionCreators = { doREQUEST_IDENTITY_SESSION_ACTIVITY };

// Tells the server the user is here (`activity_reporter.ts` decides when). A background report: it
// answers its caller directly with an `ActivityReportOutcome` and never through REQUEST_COMPLETE or
// REQUEST_ERROR, so it leaves no request-state entry, raises no error screen, shows no loading
// indicator and is never replayed. Its request carries no body.
//
// The report belongs to the session it was made in. It checks that session is still this tab's
// before it sends and before it publishes, so a report made before a sign-out or a sign-in as
// someone else is never sent with the next session's token, and its reply never lands in that
// session's state. A 401 for the session still current goes to the refresh, which ends the session
// the usual way if it is over; a 401 for a session already gone is ignored.
export function* requestIdentitySessionActivity({
  setRequestResult,
}: CreateDoRequestActionOutput<Record<string, never>>): SagaIterator {
  debug("saga*requestIdentitySessionActivity");
  const answer = (outcome: ActivityReportOutcome) => setRequestResult?.(outcome as any);

  // another tab signed in or out since this tab last bound to the browser's sign-in: the refresh
  // proves whose the cookie is before anything is reported for it
  if (sessionScopeStatus() === "unbound") {
    yield put(doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT({ requestId: v4() }));
    yield take([AUTH_SET, AUTH_REFRESHING_FAILURE, AUTH_REFRESH_UNAVAILABLE]);
  }

  const scope = currentSessionScope();
  if (!scope) return answer("stale");

  const accessToken: string | null = yield call(getToken);
  if (!accessToken || !isSessionScopeCurrent(scope)) return answer("stale");

  const deadlineRequest = startDeadlineRequest();
  const result: BaseRequestResult = yield call(() =>
    baseRequest({
      method: "post",
      url: `${API_URL}/v1/web/identity-activity`,
      accessToken,
      // a hung report would hold every later one back while the server's idle clock runs out
      timeout: REFRESH_REQUEST_TIMEOUT_MS,
    }),
  );
  if (!isSessionScopeCurrent(scope)) return answer("stale");

  if (result.statusCode === 401) {
    yield put(doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT({ requestId: v4() }));
    return answer("failed");
  }
  if (!result.success) {
    debug("saga*requestIdentitySessionActivity failed", { statusCode: result.statusCode });
    return answer("failed");
  }

  const known = yield select((state: any) => state?.auth?.sessionDeadlines ?? null);
  const { deadlines, revalidate } = observeSessionDeadlines({
    known,
    nonce: scope.nonce,
    payload: result.payload,
    request: deadlineRequest,
  });
  // an ignored reply hands back what was known: nothing to store again. One that changes only the
  // policy, a dormant reply after enforcement say, comes back as a new record like any other change.
  if (deadlines && deadlines !== known) yield put(doAUTH_SESSION_DEADLINES_SET(deadlines));
  if (revalidate) yield put(doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT({ requestId: v4() }));
  return answer("acknowledged");
}

export function* identitySessionActivitySaga() {
  yield takeEvery(REQUEST_IDENTITY_SESSION_ACTIVITY, requestIdentitySessionActivity);
}
